import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { InlineSpinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Gauge, Zap, Sparkles, DownloadCloud, AlertTriangle } from "lucide-react";

const RANGES = [["today", "Today"], ["7d", "7 Days"], ["month", "This Month"], ["custom", "Custom"]];
const money = (v) => `$${(v || 0).toFixed(4)}`;

export default function AiUsageCost() {
  const [range, setRange] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = range === "custom" && start ? `&start=${start}T00:00:00+00:00&end=${end || start}T23:59:59+00:00` : "";
      const { data } = await api.get(`/copilot/usage/summary?range=${range}${qs}`);
      setData(data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [range, start, end]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/copilot/usage/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const saveSettings = async () => {
    try {
      const payload = {};
      ["max_internal_results", "max_external_pages", "context_token_target", "response_length_target", "daily_soft_cost", "monthly_soft_cost"]
        .forEach((k) => { if (settings[k] !== undefined && settings[k] !== "") payload[k] = Number(settings[k]); });
      const { data } = await api.patch("/copilot/usage/settings", payload);
      setSettings(data); toast.success("Cost safeguards saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const Metric = ({ label, value, sub, icon: Icon, cls }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`usage-metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <p className="text-xs text-slate-400 font-medium flex items-center gap-1.5">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</p>
      <p className={`text-2xl font-bold tabular-nums ${cls || "text-slate-800"}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div data-testid="ai-usage-cost">
      <PageHeader title="AI Usage & Cost" description="Communications Copilot & Support Assistant generation volume, cost-efficiency and estimated spend." />
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {RANGES.map(([v, l]) => (
          <Button key={v} size="sm" variant={range === v ? "default" : "outline"} onClick={() => setRange(v)}
            className={range === v ? "bg-[#006090]" : ""} data-testid={`usage-range-${v}`}>{l}</Button>
        ))}
        {range === "custom" && (
          <>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-40" data-testid="usage-start" />
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-40" data-testid="usage-end" />
            <Button size="sm" onClick={load} data-testid="usage-apply">Apply</Button>
          </>
        )}
      </div>

      {loading || !data ? <div className="py-16 flex justify-center"><InlineSpinner /></div> : (
        <>
          {data.warnings?.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 mb-4 text-sm text-amber-800" data-testid="usage-warnings">
              {data.warnings.map((w, i) => <p key={i} className="flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />{w}</p>)}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Metric label="Total Generations" value={data.total_generations} icon={Gauge} />
            <Metric label="No-AI" value={data.no_ai} sub={`${data.no_ai_percentage}% required no LLM`} icon={Zap} cls="text-slate-700" />
            <Metric label="Standard AI" value={data.standard_ai} icon={Sparkles} cls="text-[#0D9488]" />
            <Metric label="Advanced AI" value={data.advanced_ai} icon={Sparkles} cls="text-indigo-600" />
            <Metric label="Estimated Cost" value={money(data.estimated_cost)} sub={data.cost_basis} cls="text-emerald-600" />
            <Metric label="Avg Cost / AI Draft" value={money(data.avg_cost_per_ai_draft)} />
            <Metric label="Certified Web Fetches" value={data.certified_web_fetches} icon={DownloadCloud} />
            <Metric label="Cache Hit Rate" value={`${data.cache_hit_rate}%`} sub={`${data.cache_hits} hits / ${data.cache_misses} miss`} />
            <Metric label="Input Tokens" value={(data.input_tokens || 0).toLocaleString()} />
            <Metric label="Output Tokens" value={(data.output_tokens || 0).toLocaleString()} />
          </div>

          <Tabs defaultValue="feature">
            <TabsList data-testid="usage-breakdown-tabs">
              {["feature", "user", "group", "model", "purpose", "mode"].map((b) => (
                <TabsTrigger key={b} value={b} data-testid={`usage-bd-${b}`}>{b[0].toUpperCase() + b.slice(1)}</TabsTrigger>
              ))}
            </TabsList>
            {["feature", "user", "group", "model", "purpose", "mode"].map((b) => (
              <TabsContent key={b} value={b} className="mt-3">
                <div className="rounded-xl border border-slate-200 bg-white divide-y">
                  {Object.entries(data.breakdowns?.[b] || {}).length === 0
                    ? <p className="p-4 text-sm text-slate-400">No data in range.</p>
                    : Object.entries(data.breakdowns[b]).sort((a, z) => z[1] - a[1]).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="text-slate-600 truncate">{k || "—"}</span>
                        <Badge variant="outline">{v}</Badge>
                      </div>
                    ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </>
      )}

      {settings && (
        <div className="rounded-xl border border-slate-200 bg-white p-5 mt-6 max-w-2xl" data-testid="usage-safeguards">
          <h3 className="font-semibold text-slate-800 text-sm mb-3">Cost Safeguards (warnings & guardrails)</h3>
          <div className="grid grid-cols-2 gap-3">
            {[["max_internal_results", "Max internal retrieval results"], ["max_external_pages", "Max external retrieved pages"],
              ["context_token_target", "Context token target"], ["response_length_target", "Response length target (chars)"],
              ["daily_soft_cost", "Daily soft-cost warning ($)"], ["monthly_soft_cost", "Monthly soft-cost warning ($)"]].map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs">{l}</Label>
                <Input type="number" value={settings[k] ?? ""} onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.value }))}
                  className="h-9 mt-1" data-testid={`safeguard-${k}`} />
              </div>
            ))}
          </div>
          <Button className="bg-[#006090] hover:bg-[#004E75] mt-4" onClick={saveSettings} data-testid="save-safeguards">Save Safeguards</Button>
        </div>
      )}
    </div>
  );
}
